import { useState, useEffect } from 'react';
import AppLayout from '@/components/layout/AppLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { FileText, TrendingUp, AlertCircle } from 'lucide-react';
import { formatCurrency, formatDate } from '@/lib/format';

const Reports = () => {
  const [reports, setReports] = useState<any[]>([]);

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    const { data } = await supabase
      .from('runs' as any)
      .select('*')
      .eq('status', 'complete')
      .order('completed_at', { ascending: false })
      .limit(10);

    if (data) {
      setReports(data as any[]);
    }
  };

  return (
    <AppLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Reports</h1>
          <p className="text-muted-foreground">
            Generated valuation reports and summaries
          </p>
        </div>

        {reports.length === 0 ? (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                No Reports Yet
              </CardTitle>
              <CardDescription>
                Run valuations to generate reports
              </CardDescription>
            </CardHeader>
          </Card>
        ) : (
          <div className="grid gap-4">
            {reports.map((report) => {
              const summary = report.summary || {};
              return (
                <Card key={report.id}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <FileText className="h-5 w-5" />
                          Valuation Report
                        </CardTitle>
                        <CardDescription>
                          Completed {formatDate(report.completed_at)}
                        </CardDescription>
                      </div>
                      <Badge variant="secondary">
                        {summary.confidence || 90}% Confidence
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Key Metrics */}
                    <div className="grid gap-4 md:grid-cols-3">
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">NPV (P50)</p>
                        <p className="text-xl font-bold">
                          {formatCurrency(summary.npv || 0)}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">Confidence Band</p>
                        <p className="text-sm font-semibold">
                          {formatCurrency(summary.ci_low_npv || 0)} - {formatCurrency(summary.ci_high_npv || 0)}
                        </p>
                        <p className="text-xs text-muted-foreground">P10 - P90 range</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">IRR</p>
                        <p className="text-xl font-bold flex items-center gap-2">
                          {summary.irr || '0.00'}%
                          <TrendingUp className="h-4 w-4 text-green-500" />
                        </p>
                      </div>
                    </div>

                    {/* Product Mix */}
                    {summary.product_mix && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium">Product Mix</p>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(summary.product_mix as Record<string, number>).map(([product, share]) => (
                            <Badge key={product} variant="outline">
                              {product}: {Math.round((share as number) * 100)}%
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Flags */}
                    {summary.flags && summary.flags.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium flex items-center gap-2">
                          <AlertCircle className="h-4 w-4" />
                          Flags
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {summary.flags.map((flag: string) => (
                            <Badge key={flag} variant="destructive">
                              {flag.replace(/-/g, ' ').toUpperCase()}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default Reports;
